import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Contract, ethers, formatEther, parseEther } from "ethers";
import { toast } from "react-toastify";
import { poolContract } from "../../../Ethereum/Constant/addresses";
import { poolABI, ERC20ABI } from "../../../Ethereum/Constant/abis";
import { contractInstance, signer } from "../../../Ethereum/Connect/config";
import Loading from "../../Loading/Loading";
import { useLanguage } from "../../LanguageContext";
import * as API from "../../../Utils/Services/api";
import translation from "../../Translations/translation.json";
import { Helmet } from "react-helmet-async";
import SeoJson from "../../SeoJson";
import getTokenBalance from "../../../Utils/getTokenBalance";
import { Dropdown } from "react-bootstrap";
import RemoveLiquidityV3 from "./RemoveLiquidityV3";

const RemoveLiquidity = () => {
  const { toggleLanguage, language } = useLanguage();

  const translate = (key) => {
    const keys = key.split(".");
    return (
      keys.reduce((obj, keyPart) => obj?.[keyPart], translation[language]) ||
      key
    );
  };
  const navigate = useNavigate();
  const url = "https://powdex.io/removeLiquidity";
  const title = `${translate("titleBar.removeLiquidity")}`;
  // "Remove Liquidity — ETHW Pools | PoWDex";
  const desc =
    "Remove your liquidity from EthereumPoW (ETHW) pools on PoWDex. Enter your LP contract address and withdraw your funds securely.";

  const [liquidityAddress, setLiquidityAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLiquidityRemoved, setShowLiquidityRemoved] = useState(false);
  const [version, setVersion] = useState("v3");

  const removeHandler = async () => {
    if (!sessionStorage.getItem("account")) {
      // toast.error("Please connect metamask wallet");
      toast.error(`${translate("pleaseConnectMetamask")}`);
      return;
    }
    try {
      setLoading(true);
      const contract = await contractInstance(liquidityAddress, poolABI);
      const tokenAddress = await contract.token();
      const liquidityTokenAddress = await contract.liquidityToken();
      const userSigner = await signer();
      const walletAddress = await userSigner.getAddress();
      const liquidityToken = new Contract(
        liquidityTokenAddress,
        ERC20ABI,
        userSigner
      );
      const lpBalance = await liquidityToken.balanceOf(userSigner);

      // console.log("lpBalance", formatEther(lpBalance));
      const formattedLpBalance = formatEther(lpBalance);

      // toast.info("Transaction sent! Waiting for confirmation...");
      toast.info(`${translate("txPending")}`);
      const tx = await contract.removeLiquidity(lpBalance);
      const receipt = await tx.wait();

      let removeEthwAmount = 0;
      let removeTokenAmount = 0;
      const iface = new ethers.Interface(poolABI);

      receipt.logs.forEach((log) => {
        try {
          const parsed = iface.parseLog(log);
          removeEthwAmount = parsed.args[1];
          removeTokenAmount = parsed.args[2];
        } catch (e) {
          console.log("Error in fetching remove events");
        }
      });

      // const payload = {
      //   ethAmount: Number(formattedLpBalance),
      //   walletAddress,
      //   poolAddress: liquidityAddress,
      // };

      const ethwReserve = await contract.ethwReserve();
      const tokenReserve = await contract.tokenReserve();
      const createdBlock = receipt.blockNumber;
      const createdTx = receipt.hash;
      const getTokenBal = await getTokenBalance(tokenAddress);

      const payload = {
        wallet: walletAddress,
        ethwReserve: parseFloat(ethers.formatEther(ethwReserve.toString())),
        tokenReserve: parseFloat(ethers.formatEther(tokenReserve.toString())),
        removeEthwAmount: parseFloat(
          ethers.formatEther(removeEthwAmount.toString())
        ),
        removeTokenAmount: parseFloat(
          ethers.formatEther(removeTokenAmount.toString())
        ),
        createdBlock: createdBlock,
        createdTx: createdTx,
        tokenBalance: getTokenBal,
      };

      const resp = await API.removeLiquidity(payload, liquidityAddress);

      if (resp.data.status) {
        // toast.success("Liquidity pool removed!");
        toast.success(`${translate("liquidityRemoved")}`);
        setLiquidityAddress("");
        setShowLiquidityRemoved(true);
      } else {
        toast.error(resp.data.message);
        setShowLiquidityRemoved(false);
      }
      setLoading(false);
    } catch (err) {
      // toast.error("Please enter valid liquidity contract address");
      toast.error(err.reason);
      setLoading(false);
      setShowLiquidityRemoved(false);
    }
  };
  const handleGoBack = () => {
    navigate(-1);
  };

  const closeModal = () => {
    setShowLiquidityRemoved(false);
  };
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />

        {/* OG / Twitter */}
        <meta property="og:site_name" content="PoWDex" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta
          property="og:image"
          content="https://powdex.io/og-remove-liquidity.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* WebApplication schema */}
      <SeoJson
        data={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "PoWDex Remove Liquidity",
          url,
          operatingSystem: "Web",
          applicationCategory: "FinanceApplication",
          description: desc,
        }}
      />

      {/* HowTo schema for removing liquidity */}
      <SeoJson
        data={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to remove liquidity on PoWDex (ETHW)",
          step: [
            {
              "@type": "HowToStep",
              name: "Enter liquidity contract address",
              text: "Paste your LP contract address in the input field.",
            },
            {
              "@type": "HowToStep",
              name: "Confirm removal",
              text: "Click Remove and confirm the transaction in your wallet.",
            },
            {
              "@type": "HowToStep",
              name: "Receive tokens",
              text: "Your tokens are returned to your wallet after the transaction confirms.",
            },
          ],
        }}
      />

      <main>
        <div className=" view-positions-pg add-liquidity-pg remove-liquidity-page">
          <div className="gradient-bg-circle-2"> </div>
          <div className="gradient-bg-circle"></div>{" "}
          <div className="container">
            <div className="page-width-vp">
              <div className="remove-liq-version">
                <Dropdown>
                  <Dropdown.Toggle
                    variant="none"
                    id="version-dropdown"
                    className="btn text-light add-liquidity-sel-token modal-btn-back-css remove-version-btn"
                  >
                    {version}
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="dropdown-menu-selection-v2-v3">
                    <Dropdown.Item onClick={() => setVersion("v2")}>
                      {/* New v2 position */}
                      {translate("addLiquidity.newv2Position")}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setVersion("v3")}>
                      {translate("addLiquidity.newv3Position")}
                      {/* New v3 position */}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="createpositons-pg remove-liquidity-pg ">
                <div className="migrate-pg-heading">
                  <div>
                    <i onClick={handleGoBack} className="fa fa-arrow-left"></i>
                  </div>
                  <div className="add-liquidity-heading">
                    {translate("removeLiquidity.heading")}
                  </div>
                  <div></div>
                </div>

                {version === "v2" ? (
                  <div className="migrate-pg-body">
                    <p>{translate("removeLiquidity.enterAddress")}</p>
                    <div className="div-add-positons-btn">
                      <div className="add-postions-form-btn selected-token-btn">
                        <input
                          type="text"
                          name="userToken"
                          className="form-control search-input-box search-input-box-liqui pt-2 pb-2"
                          value={liquidityAddress}
                          placeholder={translate(
                            "removeLiquidity.enterAddress"
                          )}
                          onChange={(e) => setLiquidityAddress(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    <div className="get-started-btn mt-4 remove-btn">
                      <button
                        className="text-white "
                        onClick={removeHandler}
                        disabled={loading}
                      >
                        {loading ? (
                          <Loading />
                        ) : (
                          translate("removeLiquidity.remove")
                        )}
                      </button>{" "}
                    </div>
                  </div>
                ) : (
                  <>
                    <RemoveLiquidityV3 />
                  </>
                )}
              </div>
            </div>{" "}
            {showLiquidityRemoved && (
              // <div className="modal show d-block" tabIndex="-1" role="dialog">
              <div
                className="modal modal-backcss show d-block"
                id="staticBackdrop"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
                tabindex="-1"
                aria-labelledby="staticBackdropLabel"
                aria-hidden="true"
              >
                <div
                  className="modal-dialog   modal-dialog-centered"
                  role="document"
                >
                  <div className="modal-content text-center">
                    <div className="modal-header">
                      <h5 className="modal-title" style={{ color: "#ebebeb" }}>
                        {/* Liquidity Remove */}
                        {translate("liquidityRemove")}{" "}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={closeModal}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p style={{ marginBottom: "0", color: "#fefefe" }}>
                        {/* Liquidity removed successfully! */}
                        {translate("liquidityRemovedSuccess")}{" "}
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        style={{
                          backgroundColor: "#00a8e8",
                          color: "#fff",
                          fontWeight: "500",
                        }}
                        type="button"
                        className="btn"
                        onClick={closeModal}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default RemoveLiquidity;
